/**
 * dailyReportService.js - 日報データの API 通信
 *
 * server.js の /api/dailyreport エンドポイントと対応する。
 * 開発時は package.json の "proxy" 設定により http://localhost:3001 へ転送される。
 */

/**
 * 指定日付・店舗・レジの日報データを取得する。
 * データがなければ null を返す。
 * @param {string} yyyymmdd  - 日付 (YYYYMMDD形式)
 * @param {number|string} shopNo  - 店舗番号
 * @param {number|string} regiNo  - レジ番号
 * @returns {Promise<object|null>}
 */
export async function fetchDailyReport(yyyymmdd, shopNo, regiNo) {
  const params = new URLSearchParams({ YYYYMMDD: yyyymmdd, ShopNo: shopNo, RegiNo: regiNo });
  const res = await fetch(`/api/dailyreport?${params}`);
  if (!res.ok) throw new Error(`日報取得失敗: ${res.status}`);
  const json = await res.json();
  return json.data;
}

/**
 * 指定月・店舗・レジの日報一覧を取得する。
 * @param {number|string} shopNo  - 店舗番号
 * @param {number|string} regiNo  - レジ番号
 * @param {string} yearMonth      - 対象月 (YYYYMM形式)
 * @returns {Promise<object[]>}
 */
export async function fetchMonthlyReports(shopNo, regiNo, yearMonth) {
  const params = new URLSearchParams({ ShopNo: shopNo, RegiNo: regiNo, yearMonth });
  const res = await fetch(`/api/dailyreport/monthly?${params}`);
  if (!res.ok) throw new Error(`月次データ取得失敗: ${res.status}`);
  const json = await res.json();
  return json.data;
}

/**
 * 全レジ情報（ShopNo, ShopName, RegiNo, RegiName）を取得する。
 * @returns {Promise<object[]>}
 */
export async function fetchRegiInfo() {
  const res = await fetch('/api/regiinfo');
  if (!res.ok) throw new Error(`レジ情報取得失敗: ${res.status}`);
  const json = await res.json();
  return json.data;
}

/**
 * 全店舗の指定月データを取得する。
 * @param {string} yearMonth - 対象月 (YYYYMM形式)
 * @returns {Promise<object[]>}
 */
export async function fetchAllShopsMonthlyReports(yearMonth) {
  const params = new URLSearchParams({ yearMonth });
  const res = await fetch(`/api/dailyreport/allshops?${params}`);
  if (!res.ok) throw new Error(`全店舗月次データ取得失敗: ${res.status}`);
  const json = await res.json();
  return json.data;
}

/**
 * 日報データを保存する（INSERT or UPDATE はサーバー側で判定）。
 * @param {object} payload - DB に保存するデータ
 * @returns {Promise<void>}
 */
export async function saveDailyReport(payload) {
  const res = await fetch('/api/dailyreport', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new Error(json.error || `日報保存失敗: ${res.status}`);
  }
}
